import { isBefore } from "date-fns";
import { parseDate } from "helpers";
import { LineitemRunningStateServer } from "model/lineitems";
import React from "react";
import {
  isCurrentCampaignBudget,
  useAllCampaignBudgets,
} from "redux/campaignsBudgets/selectors";
import { getRunningCampaignsList } from "redux/campaignStatus/campaignStatusSelectors";
import {
  getCampaignsLineitemsStatusServerObject,
  ICampaignsLineitemStatusServerObject,
} from "redux/campaignStatus/statusServerSelectors";
import { useSelector } from "redux/hooks";
import { CampaignPageNotificationType, NotificationReason } from "../types";
import { getNotificationId } from "./helper";
import { CampaignWithDatedBudgets } from "./useCampaignBudgetNotifications";

const getLineitemNotifications = (
  runningCampaignsList: CampaignWithDatedBudgets[],
  campaignsLineitemStatusObject: ICampaignsLineitemStatusServerObject
) => {
  const notifications: Array<CampaignPageNotificationType> = [];
  const reason = NotificationReason.lineitemSpendOver90;
  const { Running } = LineitemRunningStateServer;
  const today = new Date();

  runningCampaignsList.forEach((campaign) => {
    if (campaign.datedBudgets?.length) return;

    const lineitems = campaign.lineitems;
    const lineitemsStatusObject =
      campaignsLineitemStatusObject[campaign.id]?.lineitems;

    if (!lineitems?.length || !lineitemsStatusObject) return;

    lineitems.forEach((lineitem) => {
      const status = lineitemsStatusObject[lineitem.id] ?? {};
      const { spent } = status;
      const runState = status?.lineitemState?.runState;
      const finishDate = parseDate(lineitem.finish);
      const endsInTheFuture = isBefore(today, finishDate as Date);

      if (
        lineitem.budget > 0 &&
        endsInTheFuture &&
        runState === Running &&
        spent &&
        spent > 0 &&
        spent > 0.9 * lineitem.budget
      ) {
        notifications.push({
          id: getNotificationId({ campaign, lineitem, reason }),
          campaignId: campaign?.id,
          campaignName: campaign?.name,
          reason,
          lineitemId: lineitem?.id,
          lineitemName: lineitem?.name,
        });
      }
    });
  });

  return notifications;
};

export default function useLineitemSpendNotifications() {
  const budgetsMap = useAllCampaignBudgets();
  const campaignsList: CampaignWithDatedBudgets[] = useSelector(
    getRunningCampaignsList
  ).map((c) => {
    return {
      ...c,
      datedBudgets: budgetsMap[c.id] || [],
      currentBudget: budgetsMap[c.id]?.find(isCurrentCampaignBudget),
    };
  });

  const campaignsStatusObject = useSelector(
    getCampaignsLineitemsStatusServerObject
  );

  const lineitemNotifications: Array<CampaignPageNotificationType> =
    React.useMemo(
      () => getLineitemNotifications(campaignsList, campaignsStatusObject),
      [campaignsList, campaignsStatusObject]
    );

  return lineitemNotifications;
}
